/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useLocation, useParams } from 'react-router';
import { CluTestSetDetailsTab } from 'src/clu/appShell/CluTestSetDetailsTab';
import { confusionMatrixCluLearnMore } from 'src/clu/cluExternalLinks';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { ModelConfusionMatrix } from 'src/modules/modelEvaluation/ModelConfusionMatrix';
import { ModelDetailsCard } from 'src/modules/modelEvaluation/ModelDetailsCard';
import { ModelEvaluationTable } from 'src/modules/modelEvaluation/ModelEvaluationTable';
import { ModelGuidanceCardMessages } from 'src/modules/modelGuidance/components/modelGuidanceCardMessages';
import { ConfusionMatrixTab } from 'src/modules/modelGuidance/components/tabs/ConfusionMatrixTab';
import { DataDistributionTab } from 'src/modules/modelGuidance/components/tabs/DataDistributionTab';
import { ModelEvaluationTab } from 'src/modules/modelGuidance/components/tabs/ModelEvaluationTab';
import { OverviewTab } from 'src/modules/modelGuidance/components/tabs/OverviewTab';
import { SchemaType } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';

type ModelPerformanceModalDto = {
    tabKey: string;
    isModalOpen: true;
    modelName: string;
    documentName: string;
    selectedRowKey: string;
    showIncorrectPredictionsOnly: boolean;
};

const messages = defineMessages({
    overview: { id: 'clu.modelDetailsPage.overview', defaultMessage: 'Overview' },
    modelType: { id: 'clu.modelDetailsPage.modelType', defaultMessage: 'Model Type' },
    intentsPivot: { id: 'clu.modelDetailsPage.intentsPivot', defaultMessage: 'Intents' },
    entitiesPivot: { id: 'clu.modelDetailsPage.entitiesPivot', defaultMessage: 'Entities' },
    intentColumnName: { id: 'clu.modelDetailsPage.intentColumnName', defaultMessage: 'Intent name' },
    entityColumnName: { id: 'clu.modelDetailsPage.entityColumnName', defaultMessage: 'Entity name' },
    testSetDetails: { id: 'clu.modelDetailsPage.testSetDetails', defaultMessage: 'Test set details' },
    newConfusionMatrix: { id: 'clu.modelDetailsPage.newConfusionMatrix', defaultMessage: 'Confusion matrix' },
    learnMore: { id: 'clu.modelDetailsPage.learnMore', defaultMessage: 'Learn more about confusion matrices.' },
    confusionMatrix: { id: 'clu.modelDetailsPage.confusionMatrix', defaultMessage: 'Test set confusion matrix' },
    datasetDistribution: { id: 'clu.modelDetailsPage.datasetDistribution', defaultMessage: 'Dataset distribution' },
    intentTableTitle: { id: 'clu.modelDetailsPage.intentTableTitle', defaultMessage: 'Intent performance metrics' },
    entityTableTitle: { id: 'clu.modelDetailsPage.entityTableTitle', defaultMessage: 'Entity performance metrics' },
    emptyIntentsMessage: { id: 'clu.modelDetailsPage.emptyIntentsMessage', defaultMessage: 'No intents available' },
    emptyEntitiesMessage: { id: 'clu.modelDetailsPage.emptyEntitiesMessage', defaultMessage: 'No entities available' },
    modelPerformance: { id: 'clu.modelDetailsPage.modelPerformance', defaultMessage: 'Model Performance' },
    confusionMatrixTabDescription: {
        id: 'clu.modelDetailsPage.confusionMatrixTabDescription',
        defaultMessage: `A confusion matrix is a specific table layout that visualizes the performance of an algorithm.
 All correct predictions are located in the diagonal of the table (highlighted in bold), so it is easy to visually
 inspect the table for prediction errors, as they will be represented by values outside the diagonal.`
    },
    confusionMatrixEmptyStateMsg: {
        id: 'clu.modelDetailsPage.confusionMatrixEmptyStateMsg',
        defaultMessage:
            "Your model's training is in progress/has failed. The confusion matrix will appear here once training is completed successfully."
    },
    confusionMatrixErrorStateMsg: {
        id: 'clu.modelDetailsPage.confusionMatrixErrorStateMsg',
        defaultMessage: 'Please make sure you include 10 or more examples (utterances).'
    }
});

const { styleDiv } = getStylistV2('ModelDetailsPage');

const ModelEvaluationTableWindow = styleDiv('ModelEvaluationTableWindow', { height: '100%', overflowY: 'auto' });

export const CluModelDetailsPage = observer(() => {
    const { localize } = useLocalization();
    const location = useLocation();
    const { serviceName } = getPortalFromUrl();
    const { isAccessibleMode } = useZoomLevel();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { rootStore, dispatcher } = useGlobalState();

    const { modelName } = useParams<{ modelName: string }>();
    const [pivotTabsDisplay, setPivotTabsDisplay] = React.useState('block');
    const [modelType, setModelType] = React.useState<SchemaType>('Intents');
    const [activeModelTypePivot, setActiveModelTypePivot] = React.useState<'intents' | 'entities'>('intents');
    const [selectedPivotKey, setSelectedPivotKey] = React.useState<string>('Overview');
    const [entityFilteredLabelInTestSetTab, setEntityFilteredLabelInTestSetTab] = React.useState<string>(null);
    const [intentFilteredLabelInTestSetTab, setIntentFilteredLabelInTestSetTab] = React.useState<string>(null);
    const [entityFilteredPredictionInTestSetTab, setEntityFilteredPredictionInTestSetTab] = React.useState<string>(null);
    const [intentFilteredPredictionInTestSetTab, setIntentFilteredPredictionInTestSetTab] = React.useState<string>(null);
    const [modelPerformanceModalInfo, setModelPerformanceModalInfo] = React.useState(location.state as ModelPerformanceModalDto);

    const currentProject = rootStore.currentCluProjectStore;
    const isOrchestrationProject = currentProject.projectType === 'Orchestration';
    const model = currentProject.trainedModelStoreHouse.items.find(m => m.name === modelName);
    const evaluation = activeModelTypePivot === 'intents' ? model?.intentEvaluationStore : model?.entityEvaluationStore;
    const isPageLoading = model && model.evaluationTracker.state !== 'completed' && model?.modelGuidanceTracker.state !== 'completed';

    const isEvaluationDisplayable =
        !isPageLoading && (evaluation?.confusionMatrix?.length > 0 || evaluation?.performanceMetricsBreakdown?.length > 0);

    const areEvaluationMetricsInEmptyState = !isPageLoading && !evaluation?.performanceMetricsBreakdown?.length;

    React.useEffect(() => {
        if (isXLargeOrGreater) {
            setPivotTabsDisplay('block');
        } else {
            setPivotTabsDisplay('grid');
        }
    }, [isXLargeOrGreater]);

    React.useEffect(() => {
        if (model && (!evaluation || !model?.intentModelGuidanceStore)) {
            dispatcher.dispatch('getCluTrainedModelAllData', {
                toastData: {},
                trainedModelNames: [modelName],
                tracker: model?.modelGuidanceTracker
            });
        }
    }, [model, evaluation, modelName]);

    const renderOverviewTab = () => (
        <Stack tokens={{ childrenGap: 24 }} styles={{ root: { marginTop: 20 } }}>
            <ModelDetailsCard trainModelInfo={model} modelEvaluationStore={evaluation} />

            <Pivot
                linkSize="normal"
                selectedKey={activeModelTypePivot}
                onLinkClick={item => setActiveModelTypePivot(item.props.itemKey as 'intents' | 'entities')}>
                {!isOrchestrationProject && <PivotItem itemKey="entities" headerText={localize(messages.entitiesPivot)} />}
                <PivotItem itemKey="intents" headerText={localize(messages.intentsPivot)} />
            </Pivot>

            {areEvaluationMetricsInEmptyState ? (
                <EmptyStateView
                    message={localize(activeModelTypePivot === 'entities' ? messages.emptyEntitiesMessage : messages.emptyIntentsMessage)}
                />
            ) : (
                <ModelEvaluationTableWindow data-is-scrollable={!isAccessibleMode}>
                    <ModelEvaluationTable
                        items={evaluation.performanceMetricsBreakdown.filter(
                            item => item.name !== LuisConstants.Clu.NoneLabelInConfusionMatrix
                        )}
                        tableTitle={localize(activeModelTypePivot === 'intents' ? messages.intentTableTitle : messages.entityTableTitle)}
                        nameColumnTitle={localize(
                            activeModelTypePivot === 'intents' ? messages.intentColumnName : messages.entityColumnName
                        )}
                    />
                </ModelEvaluationTableWindow>
            )}
        </Stack>
    );

    const handlePivotChange = (tabKey: string) => {
        if (tabKey !== 'test-set-tab') {
            setModelPerformanceModalInfo(null);
            setEntityFilteredLabelInTestSetTab(null);
            setEntityFilteredPredictionInTestSetTab(null);
            setIntentFilteredLabelInTestSetTab(null);
            setIntentFilteredPredictionInTestSetTab(null);
        }

        setSelectedPivotKey(tabKey);

        logButtonClick(serviceName, 'ModelPerformance', tabKey, null, currentProject.id);
    };

    const renderConfusionMatrixTab = () => (
        <Stack verticalFill tokens={{ childrenGap: 34 }} styles={{ root: { marginTop: 20 } }}>
            <LuisText>
                {localize(messages.confusionMatrixTabDescription)}
                &nbsp;
                <ExternalLink href={confusionMatrixCluLearnMore}>{localize(messages.learnMore)}</ExternalLink>
            </LuisText>

            <Pivot
                linkSize="normal"
                selectedKey={activeModelTypePivot}
                onLinkClick={item => {
                    setActiveModelTypePivot(item.props.itemKey as 'intents' | 'entities');
                    item.props.itemKey === 'entities' &&
                        logButtonClick(serviceName, 'ModelPerformance', 'EntityPerformance', null, currentProject.id);
                }}>
                {!isOrchestrationProject && <PivotItem itemKey="entities" headerText={localize(messages.entitiesPivot)} />}
                <PivotItem itemKey="intents" headerText={localize(messages.intentsPivot)} />
            </Pivot>

            {isEvaluationDisplayable ? (
                <ModelConfusionMatrix
                    modelConfusionMatrix={evaluation.confusionMatrix}
                    performanceMetricsBreakdown={evaluation.performanceMetricsBreakdown}
                />
            ) : (
                <EmptyStateView
                    imageSrc={getAssetsUrl(`assets/images/customText/emptyConfusionMatrixIllustration.svg`)}
                    message={localize(model ? messages.confusionMatrixErrorStateMsg : messages.confusionMatrixEmptyStateMsg)}
                />
            )}
        </Stack>
    );

    const modelOptions: IDropdownOption[] = [
        { key: 'Intents', text: localize(messages.intentsPivot) },
        { key: 'Entities', text: localize(messages.entitiesPivot) }
    ];

    const modelTypesOptions = !isOrchestrationProject ? (
        <LuisDropdown
            options={modelOptions}
            label={localize(messages.modelType)}
            styles={{ root: { width: 246 } }}
            selectedKey={modelType}
            isSearchEnabled={false}
            onChange={(_, type) => setModelType(type.key as SchemaType)}
        />
    ) : null;

    return (
        <Page size="container-fluid" pageName="ModelDetailsPage" title={modelName}>
            {isPageLoading ? (
                <ShimmerFactory shimmerCount={10} />
            ) : (
                <Stack grow>
                    <EditableTitle title={modelName} />

                    {FeatureToggles.CluModelGuidance ? (
                        [
                            <Pivot
                                onLinkClick={link => handlePivotChange(link.props.itemKey)}
                                linkSize="large"
                                selectedKey={selectedPivotKey}
                                styles={{ root: { display: pivotTabsDisplay } }}>
                                <PivotItem itemKey="overview-tab" headerText={localize(messages.overview)}>
                                    <OverviewTab
                                        onRenderOptions={() => modelTypesOptions}
                                        currentProjectName={currentProject.name}
                                        capabilityName={currentProject.projectType}
                                        isExtractionProject={false}
                                        trainedModel={model}
                                        pivotName={modelType}
                                        modelEvaluationStore={
                                            modelType === 'Entities' ? model?.entityEvaluationStore : model?.intentEvaluationStore
                                        }
                                        onLinkToOtherTabClick={handlePivotChange}
                                        modelGuidanceStore={
                                            modelType === 'Entities' ? model?.entityModelGuidanceStore : model?.intentModelGuidanceStore
                                        }
                                    />
                                </PivotItem>
                                <PivotItem itemKey="evaluation-tab" headerText={localize(messages.modelPerformance)}>
                                    <ModelEvaluationTab
                                        pivotName={modelType}
                                        onRenderOptions={() => modelTypesOptions}
                                        isExtractionProject={false}
                                        isEvaluationDataDisplayable={isEvaluationDisplayable}
                                        modelEvaluationStore={
                                            modelType === 'Entities' ? model?.entityEvaluationStore : model?.intentEvaluationStore
                                        }
                                        modelGuidanceStore={
                                            modelType === 'Entities' ? model?.entityModelGuidanceStore : model?.intentModelGuidanceStore
                                        }
                                    />
                                </PivotItem>
                                <PivotItem itemKey="test-set-tab" headerText={localize(messages.testSetDetails)} />
                                <PivotItem itemKey="data-distribution-tab" headerText={localize(messages.datasetDistribution)}>
                                    <DataDistributionTab
                                        pivotName={modelType}
                                        onRenderOptions={() => modelTypesOptions}
                                        isExtractionProject={false}
                                        capabilityName={currentProject.projectType}
                                        modelGuidanceStore={
                                            modelType === 'Entities' ? model?.entityModelGuidanceStore : model?.intentModelGuidanceStore
                                        }
                                    />
                                </PivotItem>
                                <PivotItem itemKey="confusion-matrix-tab" headerText={localize(messages.newConfusionMatrix)}>
                                    <ConfusionMatrixTab
                                        pivotName={modelType}
                                        initialConfusionMatrixFormat="raw"
                                        onRenderOptions={() => modelTypesOptions}
                                        isEvaluationDataDisplayable={isEvaluationDisplayable}
                                        fileTypeMessage={localize(ModelGuidanceCardMessages.utterances)}
                                        trainedModel={model}
                                        serviceName="clu"
                                        modelEvaluationStore={
                                            modelType === 'Entities' ? model?.entityEvaluationStore : model?.intentEvaluationStore
                                        }
                                        onOverviewTabClick={() => handlePivotChange('overview-tab')}
                                        onCellClick={selectedLabels => {
                                            modelType === 'Entities'
                                                ? (setEntityFilteredLabelInTestSetTab(selectedLabels?.[1]),
                                                  setEntityFilteredPredictionInTestSetTab(selectedLabels?.[0]))
                                                : (setIntentFilteredLabelInTestSetTab(selectedLabels?.[1]),
                                                  setIntentFilteredPredictionInTestSetTab(selectedLabels?.[0]));
                                            handlePivotChange('test-set-tab');
                                        }}
                                    />
                                </PivotItem>
                            </Pivot>,
                            ...(selectedPivotKey === 'test-set-tab'
                                ? [
                                      <CluTestSetDetailsTab
                                          {...modelPerformanceModalInfo}
                                          selectedModelName={modelName}
                                          entityFilteredLabelInTestSetTab={entityFilteredLabelInTestSetTab}
                                          intentFilteredLabelInTestSetTab={intentFilteredLabelInTestSetTab}
                                          entityFilteredPredictionInTestSetTab={entityFilteredPredictionInTestSetTab}
                                          intentFilteredPredictionInTestSetTab={intentFilteredPredictionInTestSetTab}
                                      />
                                  ]
                                : [])
                        ]
                    ) : (
                        <Pivot
                            onLinkClick={link =>
                                logButtonClick(serviceName, 'ModelPerformance', link.props.itemKey, null, currentProject.id)
                            }
                            linkSize="large"
                            styles={{ root: { display: pivotTabsDisplay } }}>
                            <PivotItem itemKey="Overview" headerText={localize(messages.overview)}>
                                {renderOverviewTab()}
                            </PivotItem>
                            <PivotItem itemKey="ConfusionMatrix" headerText={localize(messages.confusionMatrix)}>
                                {renderConfusionMatrixTab()}
                            </PivotItem>
                        </Pivot>
                    )}
                </Stack>
            )}
        </Page>
    );
});
